import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { IModuleStore } from 'redux-dynamic-modules';
import {
    Button, Icon, Menu, MenuItemProps, Popup, Radio,
} from 'semantic-ui-react';
import { Switch } from '@inspera/component-library';
import { tl } from 'common/services/i18nService';
import newAuthorUIService from 'author/src/services/newAuthorUIService';
import { Item, Section } from 'author/src/features/questionDelivery/types';
import { ContextMenuConfig } from 'common/store/workspaces/reducer';
import { ActionMenuItem, ActionStatusService, FeatureContextMenu } from 'common/components/contextMenu/interfaces';
import ActionButtonsContainer from 'common/components/actionButtonsContainer/ActionButtonsContainer';
import { CONTENT_QTI_SECTION, KEY_CODES } from 'common/store/common/constants';
import { Categories } from 'common/types/TabTypes';
import { tabStateBuilder } from 'common/services/tabStateBuilder';
import { FeatureNamespace } from 'common/services/featureService';
import { TypeKeys } from 'common/features/table/actions';

import { TableState } from '../../features/table/reducer';
import { CommonRootState } from '../../store/typings';
import { getGenericContextMenu } from './genericMenuActions';

import 'common/styles/responsive/FeatureLoader.scss';
import * as styles from './ContextMenu.scss';
import { CommonContextState } from '../../store/context/types';
import { shouldRenderHideResponseAreaToggle } from './utils';
import { ContrastFocusVisibleContainer } from '../contrastFocusVisibleContainer/ContrastFocusVisibleContainer';
import { isQuestionMode } from '../../util/shared/sharedUtils';

type ClickHandler = (
    event: Event | React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>,
    data: MenuItemProps) => void;
type KeyPressHandler = (event: Event | React.MouseEvent<HTMLButtonElement>) => void;

interface MenuProps {
    tabIndex: string;
    borderless: string;
    role: string;
    key?: any;
    href?: string;
    target?: string;
    onClick?: ClickHandler;
    onKeyPress?: KeyPressHandler;
    disabled?: boolean;
}

interface StateProps {
    config: ContextMenuConfig;
    overlayActive: boolean;
    context: CommonContextState;
}

interface ContextMenuProps {
    [key: string]: any;

    featureState: any;
    featureId: string;
    featureName: string;
    featureContextMenu: FeatureContextMenu;
    promotedActions?: string[];
    withHeader: boolean;
    appStore: IModuleStore<CommonRootState>;
    featureNamespace: FeatureNamespace;
    hasPermissionForAction?: (action: string, featureState: any) => boolean;
    disableTabNameEditWhenNotExpanded?: any;
    actionStatusService: ActionStatusService;
}

interface DispatchProps {
    runAction: (actionCreator: any) => void;
}

export type Props = StateProps & DispatchProps & ContextMenuProps;

interface ContextState {
    readonly isOpen: boolean;
}

export class ContextMenu extends React.PureComponent<Props, ContextState> {
    private readonly contextRef = React.createRef<HTMLDivElement>();

    constructor(props: Props) {
        super(props);

        this.state = {
            isOpen: false,
        };
    }

    componentDidUpdate() {
        const { isOpen } = this.state;

        if (this.contextRef && this.contextRef.current && isOpen) {
            this.contextRef.current.focus();
        }
    }

    getClickHandler(actionMenuItem: ActionMenuItem) {
        return (event: Event | React.MouseEvent<HTMLButtonElement>) => {
            const { runAction } = this.props;

            if (event.type === 'keypress'
                && ((event as KeyboardEvent).which !== KEY_CODES.ENTER && (event as KeyboardEvent).which !== KEY_CODES.SPACE)) {
                return false;
            }

            runAction(actionMenuItem.onClick);

            this.setState({
                isOpen: false,
            });
            return undefined;
        };
    }

    onToggleActionsMenu = () => {
        this.setState({
            isOpen: !this.state.isOpen,
        });
    };

    onCloseActionMenu = () => {
        this.setState({
            isOpen: false,
        });
    };

    onOpenActionMenu = () => {
        this.setState({
            isOpen: true,
        });
    };

    getItemIcon = (item: ActionMenuItem, isActive = false) => {
        if (item.secondaryIcon && isActive) {
            if (item.secondaryIcon === 'angle right') {
                return <Icon name={item.secondaryIcon} className={styles.noMargin} />;
            }
            return <Icon name={item.secondaryIcon} />;
        }
        if (item.actionName === 'flag' && !isActive) {
            return <Icon name="flag outline" />;
        }
        return item.icon && <Icon name={item.icon} />;
    };

    getItemName = (item: ActionMenuItem, featureName = 'genericActions', isActive = false) => {
        const { featureNamespace } = this.props;
        if (item.isGeneric) {
            return tl(`${featureNamespace}.genericActions.${isActive ? item.secondaryName : item.actionName}`);
        }
        if (item.secondaryName && isActive) {
            return tl(`${featureNamespace}.${featureName}.${item.secondaryName}`);
        }
        return item.actionName !== 'help'
            ? tl(`${featureNamespace}.${featureName}.${item.actionName}`)
            : tl(`${featureNamespace}.genericActions.help`);
    };

    getItemProps = (item: ActionMenuItem, onClick: ClickHandler, onKeyPress?: KeyPressHandler, isDisabled?: boolean) => {
        const props: MenuProps = {
            onClick,
            onKeyPress,
            tabIndex: '0',
            borderless: 'true',
            role: 'button',
            disabled: isDisabled,
        };

        if (item.href) {
            props.href = item.href;
            props.role = 'button';
            props.target = '_blank';
        }
        return props;
    };

    renderFeatureContextActionMenuItems(): any[] {
        const { context } = (this.props.appStore.getState() as CommonRootState);
        const {
            featureContextMenu, featureState, featureName, config, promotedActions, overlayActive, featureId,
            disableTabNameEditWhenNotExpanded, actionStatusService, featureNamespace,
        } = this.props;
        if (!featureContextMenu) {
            return [];
        }

        const REVIEW_WORKSPACES = ['item_review_xtm', 'item_review', 'item_bank_default'];
        const requiredPropIsInState = (item: ActionMenuItem) => !(item.requiredProp && (Array.isArray(item.requiredProp)
            ? !(item.requiredProp.every((prop: any) => featureState[prop]))
            : !featureState[item.requiredProp]));
        const itemShouldNotBePartOfPromotedActions = (item: ActionMenuItem) => {
            return !(promotedActions && promotedActions.length > 0 && promotedActions.indexOf(item.actionName) > -1);
        };
        return featureContextMenu.featureContextActions
            .filter(requiredPropIsInState)
            .filter(this.hasPermission)
            .filter(itemShouldNotBePartOfPromotedActions)
            .filter((item: ActionMenuItem) => {
                const isReviewContentPreview = (featureName === 'contentPreview') && (REVIEW_WORKSPACES.includes(context.workspaceId));
                if (isReviewContentPreview && item.actionName === 'hideResponseArea') {
                    return false;
                }
                return true;
            })
            .filter((featureContextAction) => {
                const { context, session, workspaces } = (this.props.appStore.getState() as CommonRootState);
                if (featureContextAction.disallowForStudent && session.user.isStudent) {
                    return false;
                }

                if (featureContextAction.allowedWorkspaces && !featureContextAction.allowedWorkspaces.includes(context.workspaceId)) {
                    return false;
                }

                if (
                    !window.marketplaceProperties.ENABLE_CHROMIUM_PDF_GENERATOR
                    && featureContextAction.actionName === 'printQuestionAllSubmissions'
                ) {
                    return false;
                }

                if (featureName === 'table') {
                    const tableFeatureState: TableState = featureState as TableState;
                    if (overlayActive || (tableFeatureState && !tableFeatureState.tableIsEditable)) {
                        return featureContextAction.actionName === 'help';
                    }
                    if (tableFeatureState && tableFeatureState.selectedTab && featureContextAction.onClick) {
                        const selectedTabInfo = tabStateBuilder.getTabInfoById(tableFeatureState.tabsData, tableFeatureState.selectedTab);
                        switch (featureContextAction.onClick.type) {
                        case TypeKeys.TOGGLE_TABNAME_EDITING:
                            if (workspaces.workspaceOverlay) {
                                return false;
                            }
                            const tableFeatureMainIndex = workspaces.currentWorkspace.main.findIndex((mainObj: any) => {
                                return mainObj.features[0].type === featureName;
                            });
                            if (selectedTabInfo.tab.category === Categories.DEFAULT
                                || selectedTabInfo.tab.category === Categories.OVERRIDE
                                || (disableTabNameEditWhenNotExpanded(tableFeatureState.dataType) && !config.isExpanded)
                                /* if the table feature width is very small and dropdown is used then rename won't work */
                                || (workspaces.currentWorkspace.layout[tableFeatureMainIndex] <= 3
                                    && workspaces.currentWorkspace.main[tableFeatureMainIndex]
                                        .features[0].options.promotedActions.includes('expand'))
                            ) {
                                // disallow action for default or overridden tabs
                                return false;
                            }
                            break;
                        case TypeKeys.REMOVE_TAB:
                            if (selectedTabInfo.tab.category === Categories.DEFAULT
                                || selectedTabInfo.tab.category === Categories.OVERRIDE) {
                                // disallow action for default or overridden tabs
                                return false;
                            }
                            break;
                        }
                    }
                }
                return true;
            })
            .map((item: ActionMenuItem) => {
                if (featureName === 'table') {
                    const tableFeatureState: TableState = featureState as TableState;
                    if (tableFeatureState && tableFeatureState.selectedTab && item.onClick) {
                        const selectedTabInfo = tabStateBuilder.getTabInfoById(tableFeatureState.tabsData, tableFeatureState.selectedTab);
                        switch (item.onClick.type) {
                        case TypeKeys.TOGGLE_TABNAME_EDITING:
                            item.onClick.tabId = tableFeatureState.selectedTab;
                            break;
                        case TypeKeys.REMOVE_TAB:
                            item.onClick.filterId = selectedTabInfo.filterId;
                            break;
                        case TypeKeys.CLONE_TAB:
                            // eslint-disable-next-line max-len
                            item.onClick.tabName = `${tl(`${featureNamespace}.table.popup.cloneDialog.copyOf`)} ${tabStateBuilder.getTabName(selectedTabInfo.tab, featureNamespace)}`;
                            break;
                        }
                    }
                }

                const onItemClick = this.getClickHandler(item);
                const isActionActiveBoolean = actionStatusService.isActionActive(featureState, item.actionName, item.isGeneric ? featureId : null);
                const isActionDisabled = actionStatusService.isActionDisabled(featureState, item.actionName);

                return this.renderMenuItem(
                    item,
                    this.getItemProps(item, onItemClick, onItemClick, isActionDisabled),
                    isActionActiveBoolean,
                    featureName,
                );
            });
    }

    itemPropIsInUse = (item: ActionMenuItem) => item.prop && this.props[item.prop];

    hasPermission = (item: ActionMenuItem) => {
        const { featureState, hasPermissionForAction } = this.props;
        if (!item.actionType) {
            return true;
        }
        return (featureState && hasPermissionForAction)
            ? hasPermissionForAction(item.actionType, featureState)
            : false;
    };

    renderGenericActionMenuItems(): any[] {
        const { featureId, config, actionStatusService } = this.props;
        const { genericActions } = getGenericContextMenu(featureId);

        return genericActions
            .filter(this.itemPropIsInUse)
            .map((item: ActionMenuItem) => {
                const onItemClick = this.getClickHandler(item);
                const isActionActiveBoolean = actionStatusService.isActionActive(config, item.actionName);

                return this.renderMenuItem(
                    item,
                    this.getItemProps(item, onItemClick),
                    isActionActiveBoolean,
                    'genericActions',
                );
            });
    }

    renderMenuItem(item: ActionMenuItem, props: MenuProps, isActive: boolean, featureName: string) {
        return (
            <Menu.Item key={item.actionName} {...props}>
                {this.getItemIcon(item, isActive)}
                <span className={item.actionName}>
                    {this.getItemName(item, featureName, isActive)}
                </span>
            </Menu.Item>
        );
    }

    renderMenuItems(featureContextActionItems: any[], genericMenuItems: any[]) {
        return (
            <Menu vertical>
                {
                    featureContextActionItems && featureContextActionItems.length > 0
                    && (
                        <React.Fragment>
                            <h5 className={styles.toolTipHeader}>{tl('feature.contextActions')}</h5>
                            {featureContextActionItems}
                        </React.Fragment>
                    )
                }
                {genericMenuItems.length
                    ? (
                        <React.Fragment>
                            <h5 className={styles.toolTipHeader}>{tl('features.genericActions')}</h5>
                            {genericMenuItems}
                        </React.Fragment>
                    )
                    : null}
            </Menu>
        );
    }

    renderActionItems(actionItems: ActionMenuItem[]) {
        const {
            promotedActions = [], featureState, featureName, featureId,
            actionStatusService, hasPermissionForAction, context,
        } = this.props;
        const requiredPropIsInState = (item: ActionMenuItem) => !(item.requiredProp && (Array.isArray(item.requiredProp)
            ? !(item.requiredProp.every((prop: any) => featureState[prop]))
            : !featureState[item.requiredProp]));
        const ifSpecificActionsArePromotedThisItemMustBeAmongThem = (item: ActionMenuItem) => {
            return (promotedActions.length > 0 && promotedActions.indexOf(item.actionName) > -1);
        };
        const ifActionsShouldBeVisible = (item: ActionMenuItem) => {
            return (actionStatusService.isActionVisible(item.actionName));
        };

        return actionItems
            .filter(requiredPropIsInState)
            .filter(ifSpecificActionsArePromotedThisItemMustBeAmongThem)
            .filter(ifActionsShouldBeVisible)
            .map((item: ActionMenuItem, index: number) => {
                const isDisabled: boolean = (featureState && item.actionType && hasPermissionForAction)
                    ? !hasPermissionForAction(item.actionType, featureState)
                    : actionStatusService.isActionDisabled(featureState, item.actionName);

                const isActive: boolean = actionStatusService.isActionActive(featureState, item.actionName, item.isGeneric ? featureId : null);
                const isInProgress: boolean = actionStatusService.isActionInProgress(featureState, item.actionName);

                const icon = this.getItemIcon(item, isActive);
                const name = this.getItemName(item, featureName, isActive);
                const className = (item.classes || '') + (isInProgress ? ' loading ' : '');
                const isNewAuthorUIEnabled = newAuthorUIService.isNewAuthorUIEnabled();

                const props = {
                    'key': index,
                    'active': !item.useLabel && isActive,
                    'disabled': !item.onClick || isDisabled || isInProgress,
                    'onClick': this.getClickHandler(item),
                    'onKeyPress': this.getClickHandler(item),
                    'className': (isNewAuthorUIEnabled && item.isIncludedInNewUI && isDisabled) ? `${className} ${styles.disabled}` : className,
                    'aria-pressed': isActive,
                    'aria-haspopup': 'true',
                    'color': item?.color,
                };

                if (item.actionName === 'hideResponseArea') {
                    return null;
                }

                if (item.useLabel) {
                    return (
                        <Button {...props}>
                            {icon}
                            {item.useLabel && name}
                        </Button>
                    );
                }

                if (item.isRadioToggleButton) {
                    const radioButtonProps = {
                        'id': `viewReportAsCandidate_${context?.partyState?.partyName}_toggle`,
                        'disabled': !item.onClick || isDisabled,
                        'onClick': this.getClickHandler(item),
                        'onKeyPress': this.getClickHandler(item),
                        'checked': featureState.viewAsStudent,
                        'aria-pressed': isActive,
                        'aria-haspopup': 'true',
                        'label': tl(item.radioBtnLabel, { candidateName: context?.partyState?.partyName }),
                    };
                    return (
                        <div key={index} className={item?.classes}>
                            <ContrastFocusVisibleContainer>
                                <Radio toggle {...radioButtonProps} />
                            </ContrastFocusVisibleContainer>
                        </div>
                    );
                }

                if (featureName === 'setMark') {
                    if (featureState && featureState.bandedOrCriteriaMarks?.marks?.length > 0 && item.actionName === 'revert') {
                        return undefined;
                    }
                }

                return (
                    <Popup
                        key={`promotedBtn_${index}`}
                        trigger={<Button {...props} icon={icon} />}
                        content={name}
                    />
                );
            });
    }

    renderPromotedActionItems() {
        const { featureContextMenu, featureState } = this.props;
        if (!featureContextMenu || !featureState) {
            return [];
        }

        const promoted: ActionMenuItem[] = featureContextMenu.featureContextActions.filter((actionItem: ActionMenuItem) => {
            return !actionItem.isIndependent;
        });

        const promotedActionsExcludingHideResponse = promoted.filter((a) => a.actionName !== 'hideResponseArea');
        return this.renderActionItems(promotedActionsExcludingHideResponse);
    }

    renderIndependentActionButtons() {
        const {
            featureContextMenu, featureState, featureName, context,
        } = this.props;
        if (!featureContextMenu || !featureState) {
            return [];
        }

        const containsCloseSplitPreviewButton = featureContextMenu
            .featureContextActions
            .find((actionMenuItem: ActionMenuItem) => {
                return actionMenuItem.actionName === 'closeSplitPreview';
            });

        const isNewAuthorUIEnabled = newAuthorUIService.isNewAuthorUIEnabled();
        const isResourcesContext = context?.currentResourceContextItem;
        const isAudioScriptContext = context?.currentResourceContextItem?.type === 'audio_script';

        const shouldNotShowButtons = (
            isNewAuthorUIEnabled
                    && context
                    && !context.currentQtiContextItem
                    && (!isResourcesContext || (isResourcesContext && !isAudioScriptContext))
                    && !containsCloseSplitPreviewButton
                    && !isQuestionMode()
        );

        if (shouldNotShowButtons) {
            return [];
        }

        const shouldNotAllowEditAction = (
            featureName === 'contentEditor' && (
                context.isLocked || context?.currentQtiContextItem?.objectType === CONTENT_QTI_SECTION
            )
        );
        const promotedActions: ActionMenuItem[] = featureContextMenu.featureContextActions.filter((actionItem: ActionMenuItem) => {
            return (
                actionItem.isIndependent
                && (actionItem.actionType !== 'editContent' || !shouldNotAllowEditAction)
            );
        }).filter((actionItem: ActionMenuItem) => {
            if (!newAuthorUIService.isNewAuthorUIEnabled()) {
                return true;
            }
            return actionItem.isIncludedInNewUI;
        });

        return this.renderActionItems(promotedActions);
    }

    renderActionsButtons(featureContextActionItems: any, genericMenuItems: any) {
        if (newAuthorUIService.isNewAuthorUIEnabled() && this.currentQuestionNeedsUpdate()) {
            return null;
        }

        const classes = this.state.isOpen ? 'actionsButtonsGroupActive' : '';
        const actionsButtonsGroupClass = newAuthorUIService.isNewAuthorUIEnabled()
            ? 'actionsButtonsGroupLeftAligned'
            : 'actionsButtonsGroup';

        const {
            featureContextMenu, promotedActions = [], actionStatusService, featureState, featureId, featureName,
        } = this.props;
        let iocpStandalone: React.ReactNode = null;
        const { context } = this.props;
        const items = Array.isArray(context?.contentItems) ? context.contentItems : [];
        const { shouldShow, hideAction } = shouldRenderHideResponseAreaToggle({
            featureContextMenu,
            promotedActions,
            actionStatusService,
            featureState,
            featureName,
            contentItems: items,
        });
        if (
            shouldShow
            && hideAction
            && actionStatusService
            && typeof actionStatusService.isActionActive === 'function'
            && typeof actionStatusService.isActionDisabled === 'function'
        ) {
            const isActive = actionStatusService.isActionActive(featureState, 'hideResponseArea');
            const isDisabled = actionStatusService.isActionDisabled(featureState, 'hideResponseArea');
            const onToggle = this.getClickHandler(hideAction);
            iocpStandalone = (
                <div
                    key="iocp_toggle_standalone"
                    className={styles.iocpToggleContainer}
                >
                    <span>{tl('author.contentPreview.hideResponseArea')}</span>
                    <Switch id={`iocpToggle_${featureId}`} checked={isActive} onChange={onToggle as any} size="small" disabled={isDisabled} />
                </div>
            );
        }

        return (
            <div className={`${actionsButtonsGroupClass} ${classes}`}>
                {this.renderIndependentActionButtons()}
                {iocpStandalone}
                {
                    !newAuthorUIService.isNewAuthorUIEnabled()
                    && (featureContextActionItems.length || genericMenuItems.length)
                        ? (
                            <Button.Group basic size="small">
                                {this.renderPromotedActionItems()}
                                <ActionButtonsContainer
                                    triggerComponent={(
                                        <Button
                                            aria-label={tl('feature.actionsButtonMenu')}
                                            aria-haspopup="true"
                                            icon="ellipsis horizontal"
                                            onClick={this.onToggleActionsMenu}
                                        />
                                    )}
                                    menuComponent={(
                                        <div className={styles.toolTip} tabIndex={-1} ref={this.contextRef}>
                                            {this.renderMenuItems(featureContextActionItems, genericMenuItems)}
                                        </div>
                                    )}
                                    onOpen={this.onOpenActionMenu}
                                    onClose={this.onCloseActionMenu}
                                />
                            </Button.Group>
                        )
                        : null
                }
            </div>
        );
    }

    currentQuestionNeedsUpdate() {
        const currentQtiContextItem = this.props.context.currentQtiContextItem;
        const assessmentData = this.props.context.assessmentData;
        if (
            !currentQtiContextItem
            || !currentQtiContextItem.objectId
            || !assessmentData
            || !assessmentData.sections
        ) {
            return false;
        }

        const objectId = currentQtiContextItem.objectId;

        const items = assessmentData.sections?.map((section: Section) => {
            return [...section.items];
        }).flat();

        const foundItem = items?.find((item: Item) => {
            return item.id === objectId;
        });

        if (!foundItem) {
            return false;
        }

        return foundItem?.resource?.liveRevisionId !== foundItem?.resource?.revisionId;
    }

    render() {
        const {
            config,
            featureName,
            overlayActive,
            context,
        } = this.props;
        const featureContextActionItems = this.renderFeatureContextActionMenuItems();
        const genericMenuItems = this.renderGenericActionMenuItems();
        const independentMenuItems = this.renderIndependentActionButtons();
        const featureInOverlay = this.props.featureId.indexOf('overlay') > -1;
        if (!featureContextActionItems.length && !genericMenuItems.length && !independentMenuItems.length) {
            return null;
        }
        if (!config || !config.visible || (featureName !== 'table' && overlayActive && !featureInOverlay)) {
            return null;
        }
        return !newAuthorUIService.isNewAuthorUIEnabled() || context.workspaceId !== 'author_preview'
            ? this.renderActionsButtons(featureContextActionItems, genericMenuItems)
            : null;
    }
}

const mapStateToProps = (state: CommonRootState, ownProps: ContextMenuProps): StateProps => ({
    config: state.workspaces.genericFeatureState[ownProps.featureId] as ContextMenuConfig,
    overlayActive: !!state.workspaces.workspaceOverlay,
    context: state.context,
});

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => bindActionCreators(
    {
        runAction: (action) => action,
    },
    dispatch,
);

export default connect<StateProps, DispatchProps, ContextMenuProps>(mapStateToProps, mapDispatchToProps)(ContextMenu);
